/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Message } from 'primereact/message';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';

import FormInput from '../../components/forms/form-input';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import FormSelect from '../../components/forms/form-select';
import FormSearchableSelect from '../../components/forms/form-searchable-select';
import { errorMasterService } from '../../services/error-master.service';
import { valueSetsService } from '../../services/value-sets.service';
import { projectsService } from '../../services/projects.service';
import { notificationMastersService } from '../../services/notification-masters.service';
import { notificationTypesService } from '../../services/notification-types.service';
import {
  errorAlertBindingService,
  type AlertRules,
  type ErrorAlertBinding,
} from '../../services/error-alert-binding.service';
import { useProjectScope } from '../../hooks/useProjectScope';
import { extractErrorMessage } from '../../utils/error-handler';

// Zod Validation Schema
const localizedMessageSchema = z.object({
  language: z
    .string()
    .min(2, 'Language code must be at least 2 characters')
    .max(2, 'Language code must be 2 characters (ISO-639-1)')
    .regex(/^[a-z]{2}$/, 'Language code must be lowercase ISO-639-1 (e.g., en, hi, te)'),
  template: z
    .string()
    .min(1, 'Template is required')
    .max(2000, 'Template must not exceed 2000 characters'),
});

// Alerting sub-form — maps to an error_alert_binding. An empty notification means
// "no alerting" (any existing binding is removed on save). Numbers are kept as
// strings here and coerced when the binding rules are built.
const alertingSchema = z.object({
  notificationMasterId: z.string().optional().or(z.literal('')),
  triggerMode: z.enum(['IMMEDIATE', 'THRESHOLD', 'DIGEST']),
  minSeverity: z.string().optional().or(z.literal('')),
  cooldownMinutes: z.string().optional().or(z.literal('')),
  thresholdCount: z.string().optional().or(z.literal('')),
  thresholdWindowMinutes: z.string().optional().or(z.literal('')),
  digestEvery: z.string().optional().or(z.literal('')),
  digestUnit: z.enum(['MINUTES', 'HOURS', 'DAYS']),
});

const errorMasterSchema = z.object({
  errorCode: z
    .string()
    .min(3, 'Error code must be at least 3 characters')
    .max(120, 'Error code must not exceed 120 characters')
    .regex(
      /^ERR\.[A-Z0-9_]+\.[A-Z0-9_]+\.[A-Z0-9_]+/,
      'Error code should follow format: ERR.<MODULE>.<COMPONENT>.<ACTION>'
    )
    .trim(),
  errorType: z.string().min(1, 'Error type is required'),
  errorSeverity: z.string().min(1, 'Error severity is required'),
  sourceType: z.string().optional().or(z.literal('')),
  sourceName: z.string().max(200, 'Must be ≤ 200 characters').optional().or(z.literal('')),
  appId: z.string().trim().optional().or(z.literal('')),
  isActive: z.boolean(),
  log: z.boolean(),
  businessArea: z.string().max(120).optional().or(z.literal('')),
  technicalArea: z.string().max(120).optional().or(z.literal('')),
  tool: z.string().max(120).optional().or(z.literal('')),
  partnerSystem: z.string().max(120).optional().or(z.literal('')),
  thirdParty: z.string().max(120).optional().or(z.literal('')),
  alerting: alertingSchema,
  messages: z
    .array(localizedMessageSchema)
    .min(1, 'At least one localized message is required'),
  devMessage: z.string().max(2000).optional().or(z.literal('')),
  helpLink: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  videoLink: z.string().url('Must be a valid URL').optional().or(z.literal('')),
});

type ErrorMasterFormData = z.infer<typeof errorMasterSchema>;
type AlertingFormData = z.infer<typeof alertingSchema>;

const TRIGGER_MODE_OPTIONS = [
  { value: 'IMMEDIATE', label: 'Immediate — on the first occurrence' },
  { value: 'THRESHOLD', label: 'After N occurrences' },
  { value: 'DIGEST', label: 'Periodic digest (batched summary)' },
];

const DIGEST_UNIT_OPTIONS = [
  { value: 'MINUTES', label: 'Minutes' },
  { value: 'HOURS', label: 'Hours' },
  { value: 'DAYS', label: 'Days' },
];

// Delivery channel -> a PrimeIcons class, shown as a hint on each notification option.
const CHANNEL_ICONS: Record<string, string> = {
  inApp: 'pi pi-bell',
  email: 'pi pi-envelope',
  sms: 'pi pi-comment',
  whatsapp: 'pi pi-whatsapp',
  push: 'pi pi-mobile',
};

const ALERTING_DEFAULTS: AlertingFormData = {
  notificationMasterId: '',
  triggerMode: 'IMMEDIATE',
  minSeverity: '',
  cooldownMinutes: '15',
  thresholdCount: '10',
  thresholdWindowMinutes: '5',
  digestEvery: '1',
  digestUnit: 'HOURS',
};

// A stored binding -> the flat alerting sub-form.
const alertingFromBinding = (b?: ErrorAlertBinding | null): AlertingFormData => {
  if (!b) return { ...ALERTING_DEFAULTS };
  const r = b.rules || ({} as AlertRules);
  return {
    notificationMasterId: b.notificationMasterId || '',
    triggerMode: (r.triggerMode as any) || 'IMMEDIATE',
    minSeverity: r.minSeverity || '',
    cooldownMinutes: String(r.cooldownMinutes ?? 15),
    thresholdCount: String(r.threshold?.count ?? 10),
    thresholdWindowMinutes: String(r.threshold?.windowMinutes ?? 5),
    digestEvery: String(r.digest?.every ?? 1),
    digestUnit: (r.digest?.unit as any) || 'HOURS',
  };
};

// The alerting sub-form -> binding rules payload.
const buildRules = (a: AlertingFormData): AlertRules => ({
  triggerMode: a.triggerMode,
  minSeverity: a.minSeverity || null,
  cooldownMinutes: Number(a.cooldownMinutes) || 15,
  threshold: {
    count: Number(a.thresholdCount) || 10,
    windowMinutes: Number(a.thresholdWindowMinutes) || 5,
  },
  digest: {
    every: Number(a.digestEvery) || 1,
    unit: a.digestUnit,
  },
});

const ErrorMasterForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({
    entity: 'errorMaster',
    basePath: '/error-master',
  });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  // Once a submit is in flight/succeeded, stop autosaving so a pending debounce
  // timer can't rewrite the draft after onSuccess has cleared it (which is what
  // made the "restore?" banner reappear right after a create/update).
  const submittedRef = useRef(false);

  const DRAFT_KEY = `error-master-draft-${isEditMode ? id : 'new'}`;

  // Draft state
  const [draftData, setDraftData] = useState<ErrorMasterFormData | null>(null);
  const [showDraftBanner, setShowDraftBanner] = useState(false);

  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
    setValue,
  } = useForm<ErrorMasterFormData>({
    resolver: zodResolver(errorMasterSchema),
    defaultValues: {
      errorCode: '',
      errorType: '',
      errorSeverity: '',
      sourceType: '',
      sourceName: '',
      appId: '',
      isActive: true,
      log: true,
      businessArea: '',
      technicalArea: '',
      tool: '',
      partnerSystem: '',
      thirdParty: '',
      alerting: { ...ALERTING_DEFAULTS },
      messages: [{ language: 'en', template: '' }],
      devMessage: '',
      helpLink: '',
      videoLink: '',
    },
    mode: 'onChange',
  });

  const formValues = watch();

  // Navigation blocker for unsaved changes
  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  // Field array for messages
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'messages',
  });

  // Fetch value-sets for dropdowns
  const { data: errorTypeItems = [] } = useQuery({
    queryKey: ['value-sets', 'errorType'],
    queryFn: () => valueSetsService.getItems('errorType', 'en'),
  });

  const { data: errorSeverityItems = [] } = useQuery({
    queryKey: ['value-sets', 'errorSeverity'],
    queryFn: () => valueSetsService.getItems('errorSeverity', 'en'),
  });

  const { data: sourceTypeItems = [] } = useQuery({
    queryKey: ['value-sets', 'sourceType'],
    queryFn: () => valueSetsService.getItems('sourceType', 'en'),
  });

  // Projects the caller may create in (platform admin = all, else own) — drives the
  // appId (owning project) scoping dropdown.
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = useMemo(
    () => accessibleProjects.map((p) => ({ value: p._id, label: p.appCode ? `${p.name} (${p.appCode})` : p.name })),
    [accessibleProjects]
  );
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => {
    if (!isEditMode && !selectedAppId && accessibleProjects.length) {
      setValue('appId', accessibleProjects[0]._id);
    }
  }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  // Notification-ENGINE masters (the binding target) + their types for labelling.
  const { data: engineNotifResp } = useQuery({
    queryKey: ['engine-notifications', 'dropdown'],
    queryFn: () => notificationMastersService.list({ limit: 200 }),
  });
  const engineNotifications = engineNotifResp?.data ?? [];

  const { data: engineTypesResp } = useQuery({
    queryKey: ['engine-notification-types', 'dropdown'],
    queryFn: () => notificationTypesService.list({ limit: 200 }),
  });
  const engineTypes = engineTypesResp?.data ?? [];
  const typeById = useMemo(
    () => Object.fromEntries(engineTypes.map((t) => [t._id, t])),
    [engineTypes]
  );

  // Fetch error master data for edit mode
  const { data: errorMaster, isLoading: fetchLoading } = useQuery({
    queryKey: ['error-master', id],
    queryFn: () => errorMasterService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  // Existing alert binding for this error (edit mode) — populates the Alerting section.
  const { data: existingBinding } = useQuery({
    queryKey: ['error-alert-binding', errorMaster?.errorCode],
    queryFn: () => errorAlertBindingService.getForError(errorMaster!.errorCode),
    enabled: isEditMode && !!errorMaster?.errorCode,
  });

  // Set breadcrumb title
  useBreadcrumbTitle(isEditMode ? errorMaster?.errorCode : null);

  // Helper function to match backend format to value-set code
  const matchValueSetCode = useCallback((backendValue: string | undefined, valueSetItems: any[]) => {
    if (!backendValue) return '';
    const exactMatch = valueSetItems.find(item => item.code === backendValue);
    if (exactMatch) return backendValue;
    const caseInsensitiveMatch = valueSetItems.find(
      item => item.code.toLowerCase() === backendValue.toLowerCase()
    );
    return caseInsensitiveMatch ? caseInsensitiveMatch.code : backendValue;
  }, []);

  // Populate form when error master (and its binding) are loaded
  useEffect(() => {
    if (errorMaster && isEditMode && errorTypeItems.length > 0 && errorSeverityItems.length > 0) {
      reset({
        errorCode: errorMaster.errorCode || '',
        errorType: matchValueSetCode((errorMaster as any).errorType, errorTypeItems),
        errorSeverity: matchValueSetCode(errorMaster.errorSeverity, errorSeverityItems),
        sourceType: matchValueSetCode((errorMaster as any).sourceType, sourceTypeItems),
        sourceName: (errorMaster as any).sourceName || '',
        appId: (errorMaster as any).appId || '',
        isActive: errorMaster.isActive ?? true,
        log: errorMaster.log ?? true,
        businessArea: errorMaster.businessArea || '',
        technicalArea: errorMaster.technicalArea || '',
        tool: errorMaster.tool || '',
        partnerSystem: errorMaster.partnerSystem || '',
        thirdParty: errorMaster.thirdParty || '',
        alerting: alertingFromBinding(existingBinding),
        messages: errorMaster.messages || [{ language: 'en', template: '' }],
        devMessage: errorMaster.devMessage || '',
        helpLink: errorMaster.helpLink || '',
        videoLink: errorMaster.videoLink || '',
      });
    }
  }, [errorMaster, existingBinding, isEditMode, reset, errorTypeItems, errorSeverityItems, sourceTypeItems, matchValueSetCode]);

  // Draft autosave (only for create mode). Skipped once a submit has started, and
  // re-checked inside the timer so an already-scheduled write is cancelled too.
  useEffect(() => {
    if (isEditMode || !isDirty || submittedRef.current) return;
    const timer = setTimeout(() => {
      if (submittedRef.current) return;
      localStorage.setItem(DRAFT_KEY, JSON.stringify(formValues));
    }, 1500);
    return () => clearTimeout(timer);
  }, [formValues, isDirty, isEditMode, DRAFT_KEY]);

  // Load draft on mount (only for create mode)
  useEffect(() => {
    if (!isEditMode) {
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        try {
          const parsed = JSON.parse(savedDraft);
          setDraftData(parsed);
          setShowDraftBanner(true);
        } catch (error) {
          console.error('Failed to parse draft:', error);
        }
      }
    }
  }, [isEditMode, DRAFT_KEY]);

  // Reconcile the alert binding after the error master is saved. Runs as a
  // follow-up (not inside the master's write) so a binding hiccup never blocks
  // or re-creates the master — the master is saved, and any binding error is
  // surfaced as a warning the user can fix by editing.
  const reconcileBinding = useCallback(
    async (errorCode: string, appId: string | undefined, alerting: AlertingFormData) => {
      const bindingId = existingBinding?.id;
      if (!alerting.notificationMasterId) {
        if (bindingId) await errorAlertBindingService.remove(bindingId);
        return;
      }
      const rules = buildRules(alerting);
      if (bindingId) {
        await errorAlertBindingService.update(bindingId, {
          notificationMasterId: alerting.notificationMasterId,
          rules,
        });
      } else {
        await errorAlertBindingService.create({
          errorCode,
          notificationMasterId: alerting.notificationMasterId,
          rules,
          appId: appId || undefined,
        });
      }
    },
    [existingBinding]
  );

  // Mutation for create/update (error master), then binding reconciliation.
  const mutation = useMutation({
    mutationFn: async (data: ErrorMasterFormData) => {
      const payload: any = {
        errorType: data.errorType,
        errorSeverity: data.errorSeverity,
        isActive: data.isActive ?? true,
        log: data.isActive ? (data.log ?? true) : false,
        appId: data.appId || undefined,
        sourceType: data.sourceType || undefined,
        sourceName: data.sourceName || undefined,
        businessArea: data.businessArea || undefined,
        technicalArea: data.technicalArea || undefined,
        tool: data.tool || undefined,
        partnerSystem: data.partnerSystem || undefined,
        thirdParty: data.thirdParty || undefined,
        messages: data.messages,
        devMessage: data.devMessage || undefined,
        helpLink: data.helpLink || undefined,
        videoLink: data.videoLink || undefined,
      };
      if (isEditMode && id) {
        await errorMasterService.update(id, payload);
      } else {
        payload.errorCode = data.errorCode;
        await errorMasterService.create(payload);
      }

      const errorCode = isEditMode ? (errorMaster?.errorCode as string) : data.errorCode;
      // The binding is a follow-up write; report its failure separately so the
      // master save is not undone or retried (which would 409 on create).
      let bindingWarning: string | null = null;
      try {
        await reconcileBinding(errorCode, data.appId, data.alerting);
      } catch (e: any) {
        bindingWarning = extractErrorMessage(e, 'Alert binding could not be saved');
      }
      return { bindingWarning };
    },
    onSuccess: ({ bindingWarning }) => {
      queryClient.invalidateQueries({ queryKey: ['error-master'] });
      queryClient.invalidateQueries({ queryKey: ['error-alert-binding'] });
      localStorage.removeItem(DRAFT_KEY);
      if (bindingWarning) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Saved with a warning',
          detail: `Error master saved, but the alert binding failed: ${bindingWarning}`,
          life: 12000,
        });
      } else {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: `Error master ${isEditMode ? 'updated' : 'created'} successfully`,
          life: 3000,
        });
      }
      setTimeout(() => { navigateToList(); }, bindingWarning ? 2500 : 1000);
    },
    onError: (error: any) => {
      // The save failed — let the user keep editing and resume autosaving.
      submittedRef.current = false;
      console.error('Error response:', error.response);
      const errorMessage = extractErrorMessage(error, `Failed to ${isEditMode ? 'update' : 'create'} error master`);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: errorMessage, life: 10000 });
    },
  });

  const isSaving = mutation.isPending;

  const onSubmit = (data: ErrorMasterFormData) => {
    // Stop autosave from re-creating the draft during/after this save.
    submittedRef.current = true;
    localStorage.removeItem(DRAFT_KEY);
    mutation.mutate(data);
  };

  const handleRestoreDraft = () => {
    if (draftData) { reset(draftData); setShowDraftBanner(false); }
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setDraftData(null);
    setShowDraftBanner(false);
  };

  // Dropdown options
  const errorTypeOptions = useMemo(
    () => errorTypeItems.map((item) => ({ value: item.code, label: item.label })),
    [errorTypeItems]
  );

  const errorSeverityOptions = useMemo(
    () => errorSeverityItems.map((item) => ({ value: item.code, label: item.label })),
    [errorSeverityItems]
  );

  const sourceTypeDropdownOptions = useMemo(
    () => sourceTypeItems.map((item) => ({ value: item.code, label: item.label })),
    [sourceTypeItems]
  );

  // `projectTag` labels each option with its project pill.
  const { projectTag } = useProjectScope(selectedAppId, accessibleProjects);

  // Engine-notification searchable-select — a two-line option: the type code as
  // the title, the notification's description beneath, channel icons as a hint,
  // and the owning project as a trailing pill.
  //
  // Scoped STRICTLY to the error master's project — notification masters are
  // always project-owned (no global/appId:null rows), so only same-project
  // notifications are offered. Before a project is chosen, show all.
  const notificationOptions = useMemo(
    () =>
      engineNotifications
        .filter((n) => {
          if (!selectedAppId) return true;
          const nApp = (n as any).appId;
          return nApp && String(nApp) === String(selectedAppId);
        })
        .map((n) => {
        const type: any = typeById[n.notificationTypeId];
        const icons = ((type?.channels as string[]) || [])
          .map((c) => CHANNEL_ICONS[c])
          .filter(Boolean);
        return {
          value: n._id,
          label: type?.notificationCode || 'Notification',
          description: n.description || undefined,
          icons,
          ...projectTag((n as any).appId),
        };
      }),
    [engineNotifications, typeById, projectTag, selectedAppId]
  );

  // Clear the selected notification if it no longer belongs to the chosen project.
  const selectedNotificationId = watch('alerting.notificationMasterId');
  const prevAppIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevAppIdRef.current;
    prevAppIdRef.current = selectedAppId;
    if (prev === undefined || prev === selectedAppId || !selectedAppId) return;
    const n = engineNotifications.find((x) => x._id === selectedNotificationId);
    const nApp = n ? (n as any).appId : undefined;
    if (nApp && nApp !== selectedAppId) setValue('alerting.notificationMasterId', '');
  }, [selectedAppId, engineNotifications, selectedNotificationId, setValue]);

  const searchNotifications = useCallback(
    async (term: string) => {
      const q = term.toLowerCase();
      return notificationOptions.filter((o) =>
        `${o.label} ${o.description || ''} ${o.tag || ''}`.toLowerCase().includes(q)
      );
    },
    [notificationOptions]
  );

  const selectedNotificationOption = useMemo(() => {
    if (!selectedNotificationId) return null;
    return notificationOptions.find((o) => o.value === selectedNotificationId) || null;
  }, [notificationOptions, selectedNotificationId]);

  const triggerMode = watch('alerting.triggerMode');
  const hasNotification = !!selectedNotificationId;

  if (fetchLoading) {
    return (
      <div className="p-3">
        <PageSkeleton />
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900 min-h-screen" data-testid="SCR-ErrorMaster-Form">
        {/* Header */}
        <ScreenHeader
          icon="pi pi-exclamation-triangle"
          title={isEditMode ? 'Edit Error Master' : 'Create New Error Master'}
          subtitle={isEditMode ? 'Update error master information' : 'Add a new error definition to the system'}
          onBack={() => handleNavigate('/error-master')}
        />

        {/* Draft Recovery Banner */}
        {showDraftBanner && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 rounded mb-3">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <i className="pi pi-exclamation-circle text-blue-600 dark:text-blue-400" style={{ fontSize: '1rem' }} />
                <span className="text-sm text-blue-800 dark:text-blue-200">You have unsaved changes from a previous session. Would you like to restore them?</span>
              </div>
              <div className="flex gap-2">
                <Button label="Restore" size="small" severity="info" onClick={handleRestoreDraft} />
                <Button label="Discard" size="small" severity="secondary" outlined onClick={handleDiscardDraft} />
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Basic Information Section */}
          <div className="ds-card p-4 mb-3">
            <h3 className="text-sm font-semibold text-ink mb-2">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Controller
                  name="errorCode"
                  control={control}
                  render={({ field }) => (
                    <FormInput {...field} label="Error Code" placeholder="ERR.MODULE.COMPONENT.ACTION" error={!!errors.errorCode} helperText={errors.errorCode?.message || 'Format: ERR.<MODULE>.<COMPONENT>.<ACTION>'} required disabled={isEditMode} />
                  )}
                />
              </div>

              <Controller
                name="errorType"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSelect {...field} value={value || ''} onChange={(e) => onChange(e.target.value)} label="Error Type" options={errorTypeOptions} error={!!errors.errorType} helperText={errors.errorType?.message} required />
                )}
              />

              <Controller
                name="errorSeverity"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSelect {...field} value={value || ''} onChange={(e) => onChange(e.target.value)} label="Error Severity" options={errorSeverityOptions} error={!!errors.errorSeverity} helperText={errors.errorSeverity?.message} required />
                )}
              />

              <Controller
                name="sourceType"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSelect {...field} value={value || ''} onChange={(e) => onChange(e.target.value)} label="Source Type" options={sourceTypeDropdownOptions} showClear error={!!errors.sourceType} helperText={errors.sourceType?.message} />
                )}
              />

              <Controller
                name="sourceName"
                control={control}
                render={({ field }) => (
                  <FormInput {...field} value={field.value || ''} label="Source Name" placeholder="e.g., core-service, auth-module" error={!!errors.sourceName} helperText={errors.sourceName?.message} />
                )}
              />

              <Controller
                name="appId"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSelect {...field} value={value || ''} onChange={(e) => onChange(e.target.value)} label="Project" options={projectOptions} placeholder="Select project" disabled={projectLocked || isEditMode} error={!!errors.appId} helperText={errors.appId?.message} />
                )}
              />

              <div className="flex items-center gap-4">
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <div className="flex items-center gap-2">
                      <Checkbox inputId="isActive" checked={value ?? false} onChange={(e) => {
                        const active = e.checked ?? false;
                        onChange(active);
                        if (!active) setValue('log', false);
                      }} />
                      <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">Active</label>
                    </div>
                  )}
                />
                <Controller
                  name="log"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <div className="flex items-center gap-2">
                      <Checkbox inputId="log" checked={value ?? false} onChange={(e) => onChange(e.checked)} disabled={!watch('isActive')} />
                      <label htmlFor="log" className={`text-sm ${watch('isActive') ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400'}`}>Enable Logging</label>
                    </div>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Context Information Section */}
          <div className="ds-card p-4 mb-3">
            <h3 className="text-sm font-semibold text-ink mb-2">Context Information (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Controller name="businessArea" control={control} render={({ field }) => (<FormInput {...field} value={field.value || ''} label="Business Area" placeholder="e.g., Authentication, Personnel Management" error={!!errors.businessArea} helperText={errors.businessArea?.message} />)} />
              <Controller name="technicalArea" control={control} render={({ field }) => (<FormInput {...field} value={field.value || ''} label="Technical Area" placeholder="e.g., Database, API, Frontend" error={!!errors.technicalArea} helperText={errors.technicalArea?.message} />)} />
              <Controller name="tool" control={control} render={({ field }) => (<FormInput {...field} value={field.value || ''} label="Tool" placeholder="e.g., MongoDB, Redis, JWT" error={!!errors.tool} helperText={errors.tool?.message} />)} />
              <Controller name="partnerSystem" control={control} render={({ field }) => (<FormInput {...field} value={field.value || ''} label="Partner System" placeholder="e.g., PaymentGateway, SMS Provider" error={!!errors.partnerSystem} helperText={errors.partnerSystem?.message} />)} />
              <Controller name="thirdParty" control={control} render={({ field }) => (<FormInput {...field} value={field.value || ''} label="Third Party" placeholder="e.g., Azure, AWS, Twilio" error={!!errors.thirdParty} helperText={errors.thirdParty?.message} />)} />
            </div>
          </div>

          {/* Alerting Section — binds this error to a notification + rules */}
          <div className="ds-card p-4 mb-3">
            <h3 className="text-sm font-semibold text-ink mb-1">Alerting (Optional)</h3>
            <p className="text-xs text-ink-muted mb-3">
              Pick a notification to raise when this error occurs, and how often. Leave the notification empty for no alerting.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Controller
                  name="alerting.notificationMasterId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSearchableSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value)}
                      label="Notification"
                      onSearch={searchNotifications}
                      initialOptions={notificationOptions}
                      selectedOption={selectedNotificationOption}
                      optionItemSize={56}
                      placeholder="Search and select a notification to send"
                      helperText="Notification-engine definition to fire. Same project or global."
                    />
                  )}
                />
              </div>

              {hasNotification && (
                <>
                  <Controller
                    name="alerting.triggerMode"
                    control={control}
                    render={({ field: { value, onChange, ...field } }) => (
                      <FormSelect {...field} value={value || 'IMMEDIATE'} onChange={(e) => onChange(e.target.value)} label="When to alert" options={TRIGGER_MODE_OPTIONS} />
                    )}
                  />

                  <Controller
                    name="alerting.minSeverity"
                    control={control}
                    render={({ field: { value, onChange, ...field } }) => (
                      <FormSelect {...field} value={value || ''} onChange={(e) => onChange(e.target.value)} label="Minimum Severity" options={errorSeverityOptions} showClear placeholder="Any severity" helperText="Only alert at or above this severity." />
                    )}
                  />

                  {triggerMode === 'THRESHOLD' && (
                    <>
                      <Controller name="alerting.thresholdCount" control={control} render={({ field }) => (<FormInput {...field} type="number" value={field.value || ''} label="After N occurrences" placeholder="10" helperText="Alert once this many happen in the window." />)} />
                      <Controller name="alerting.thresholdWindowMinutes" control={control} render={({ field }) => (<FormInput {...field} type="number" value={field.value || ''} label="Within (minutes)" placeholder="5" helperText="The rolling window the count is measured over." />)} />
                    </>
                  )}

                  {triggerMode === 'DIGEST' && (
                    <>
                      <Controller name="alerting.digestEvery" control={control} render={({ field }) => (<FormInput {...field} type="number" value={field.value || ''} label="Send every" placeholder="1" helperText="e.g. 1 = hourly, 5 = every 5 hours." />)} />
                      <Controller
                        name="alerting.digestUnit"
                        control={control}
                        render={({ field: { value, onChange, ...field } }) => (
                          <FormSelect {...field} value={value || 'HOURS'} onChange={(e) => onChange(e.target.value)} label="Interval unit" options={DIGEST_UNIT_OPTIONS} />
                        )}
                      />
                    </>
                  )}

                  {triggerMode !== 'DIGEST' && (
                    <Controller name="alerting.cooldownMinutes" control={control} render={({ field }) => (<FormInput {...field} type="number" value={field.value || ''} label="Cooldown (minutes)" placeholder="15" helperText="Minimum gap between repeat alerts." />)} />
                  )}
                </>
              )}
            </div>
          </div>

          {/* Localized Messages Section */}
          <div className="ds-card p-4 mb-3">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-semibold text-ink">Localized Messages</h3>
              <Button type="button" label="Add Language" icon="pi pi-plus" size="small" outlined onClick={() => append({ language: '', template: '' })} />
            </div>

            {fields.map((field, index) => (
              <div key={field.id} className="p-3 mb-3 bg-gray-50 dark:bg-gray-700/50 rounded">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-ink-muted">Message #{index + 1}</span>
                  {fields.length > 1 && (
                    <button aria-label="Delete" type="button" onClick={() => remove(index)} className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors">
                      <i className="pi pi-trash" style={{ fontSize: '1rem' }} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <Controller name={`messages.${index}.language`} control={control} render={({ field }) => (<FormInput {...field} label="Language Code" placeholder="en, hi, te" error={!!errors.messages?.[index]?.language} helperText={errors.messages?.[index]?.language?.message || 'ISO-639-1 (2 letters)'} required />)} />
                  <div className="md:col-span-3">
                    <Controller name={`messages.${index}.template`} control={control} render={({ field }) => (<FormInput {...field} label="Message Template" placeholder="Use {placeholderName} for dynamic values" error={!!errors.messages?.[index]?.template} helperText={errors.messages?.[index]?.template?.message || 'Use {lowerCamelCase} placeholders for dynamic content'} multiline rows={2} required />)} />
                  </div>
                </div>
              </div>
            ))}

            {errors.messages && typeof errors.messages === 'object' && !Array.isArray(errors.messages) && (
              <Message severity="error" text={(errors.messages as any).message} className="w-full mt-2" />
            )}
          </div>

          {/* Additional Information Section */}
          <div className="ds-card p-4">
            <h3 className="text-sm font-semibold text-ink mb-2">Additional Information (Optional)</h3>
            <div className="space-y-3">
              <Controller name="devMessage" control={control} render={({ field }) => (<FormInput {...field} value={field.value || ''} label="Developer Message" placeholder="Technical details for developers..." error={!!errors.devMessage} helperText={errors.devMessage?.message} multiline rows={3} />)} />
              <Controller name="helpLink" control={control} render={({ field }) => (<FormInput {...field} value={field.value || ''} label="Help Link" placeholder="https://docs.example.com/errors/..." error={!!errors.helpLink} helperText={errors.helpLink?.message} />)} />
              <Controller name="videoLink" control={control} render={({ field }) => (<FormInput {...field} value={field.value || ''} label="Video Link" placeholder="https://www.youtube.com/watch?v=..." error={!!errors.videoLink} helperText={errors.videoLink?.message} />)} />
            </div>
          </div>

          {/* Form Actions */}
          <div className="px-4 py-3 bg-canvas dark:bg-gray-900/50 border border-line flex justify-end gap-2 rounded-lg mt-3">
            <Button label="Cancel" severity="secondary" outlined onClick={() => handleNavigate('/error-master')} disabled={isSaving} size="small" />
            <Button type="submit" label={isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'} icon={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={isSaving} size="small" testId="ErrorMasterForm.Button.Submit" />
          </div>
        </form>

        {/* Leave Confirmation Dialog */}
        <DiscardChangesDialog visible={showLeaveDialog} onStay={cancelLeave} onLeave={confirmLeave} testId="ErrorMasterForm.Dialog.Leave" />
      </div>
    </>
  );
};

export default ErrorMasterForm;
